import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'environments/environment';
import mixpanel from 'mixpanel-browser';
import { ChildActivationEnd, Router } from '@angular/router';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { Subject, fromEvent } from 'rxjs';
import { Dictionary } from 'lodash';
import { UserService } from './user.service';
import Hotjar from '@hotjar/browser';

const hotjarSiteId = 3846364;
const hotjarVersionId = 6;

/**
 * This service uses Mixpanel analytics.
 *
 * See: https://docs.mixpanel.com/docs/getting-started/what-is-mixpanel
 */

@Injectable({
	providedIn: 'root',
})
export class AnalyticsService implements OnDestroy {
	private readonly mixpanelToken = environment.mixpanelAnalyticsToken;
	private readonly destroy$ = new Subject<boolean>();

	get hasOptedInTracking(): boolean {
		return mixpanel.has_opted_in_tracking();
	}

	constructor(private readonly userService: UserService, private readonly router: Router) {}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	/**
	 * Initialize Mixpanel.
	 */
	init(): void {
		mixpanel.init(this.mixpanelToken, {
			api_host: 'https://api-eu.mixpanel.com',
			debug: !environment.production,
			persistence: 'localStorage',
		});

		// TODO(aled): Uncomment when we are ready to only track on prod not staging.
		// if (!environment.production) {
		// 	mixpanel.disable();

		// 	return;
		// }

		this.handleCookieConsentOptInOut();

		this.registerOnce({ browserLanguage: navigator.language });
	}

	sendEvent(event: string, properties?: Dictionary<string | string[]>): void {
		properties ? mixpanel.track(event, properties) : mixpanel.track(event);
	}

	identify(userIdentifier: string): void {
		mixpanel.identify(userIdentifier);
	}

	reset(): void {
		mixpanel.reset();
	}

	/** Initialize Hotjar heatmap analysis. */
	private initHotjar(): void {
		if (!this.hasOptedInTracking || !environment.production) return;

		Hotjar.init(hotjarSiteId, hotjarVersionId);
	}

	private startTrackingAsyncEvents(): void {
		this.trackRouterEvents();
		this.trackAuthToken();
	}

	private registerOnce(properties: Dictionary<string>): void {
		mixpanel.register_once(properties);
	}

	private trackAuthToken(): void {
		this.userService.userToken$
			.pipe(
				tap(token => {
					if (!token) {
						this.reset();
						return;
					}

					const { unique_name, role } = token?.claims;

					if (unique_name) {
						this.identify(unique_name);

						this.registerOnce({ 'User email': unique_name });
					}

					if (role) {
						this.registerOnce({ Role: role });
					}
				}),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}

	private trackRouterEvents(): void {
		this.router.events
			.pipe(
				filter(event => event instanceof ChildActivationEnd),
				tap((event: ChildActivationEnd) => {
					this.trackPageView(event);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}

	private trackPageView(event: ChildActivationEnd): void {
		const pageName = event.snapshot.firstChild.data.title;

		if (pageName) this.sendEvent(`${pageName} page viewed`);
	}

	/**
	 * It is important we respect the user's cookie consent options.
	 *
	 * Mixpanel comes under the not necessary 'performance' category for cookie consent.
	 * We must wait until the user provides their cookie consent settings before we can begin tracking.
	 */
	private handleCookieConsentOptInOut(): void {
		// Handle existing user concent.
		fromEvent(window, 'cf_consent_loaded')
			.pipe(takeUntil(this.destroy$))
			.subscribe((event: CustomEvent) => {
				if (event.detail?.performance) {
					// We only want to send the opt_in event once.
					if (mixpanel.has_opted_out_tracking() || (!mixpanel.has_opted_out_tracking() && !mixpanel.has_opted_in_tracking())) {
						mixpanel.opt_in_tracking();
					}

					this.startTrackingAsyncEvents();

					this.initHotjar();
				} else {
					mixpanel.opt_out_tracking();
				}
			});

		// Handle new user concent.
		fromEvent(window, 'cf_consent')
			.pipe(takeUntil(this.destroy$))
			.subscribe((event: CustomEvent) => {
				if (event.detail?.performance) {
					mixpanel.opt_in_tracking();

					this.startTrackingAsyncEvents();

					this.initHotjar();
				} else {
					mixpanel.opt_out_tracking();
				}
			});
	}
}
